import React from "react";
import { Table, Space, Button, Popconfirm, Tooltip, message } from "antd";
import { EditOutlined, DeleteOutlined, ThunderboltOutlined } from "@ant-design/icons";
import moment from "moment";
import { ListComponent, BaseListComponentProps } from "../BaseComponent";
import { SearchRequest } from "../../services/api/interfaces/ApiRequest";
import {
  integrationCredentialDelete,
  integrationCredentialTest,
} from "../../services/integration";
import { Pagination } from "components/Pagination";

// Extracts a human-readable reason from an apisauce-style response, trying the
// API-provided message first, then the transport error, then the problem code.
function getResponseErrorReason(
  response: any,
  fallback = "unknown error"
): string {
  const body = response?.data as any;
  return (
    body?.message ||
    response?.originalError?.message ||
    response?.problem ||
    fallback
  );
}

type IntegrationCredentialsListProps = BaseListComponentProps & {
  credentials: any;
  pagination: any;
  loading: boolean;

  history: any;
  searchOptions: any;

  loadCredentials: (args: SearchRequest) => void;
  editItem: (item: any) => void;
};

export default class IntegrationCredentialsList extends ListComponent<IntegrationCredentialsListProps> {
  static defaultProps = {
    loading: false,
  };

  state = {
    removed: [] as string[],
    testing: null as string | null,
  };

  tableColumns = [
    {
      title: "UUID",
      dataIndex: "uuid",
      key: "uuid",
    },
    {
      title: "Shelter",
      dataIndex: ["shelter", "name"],
      key: "shelter.name",
      responsive: ["md"] as any,
    },
    {
      title: "Partner",
      dataIndex: "partner_key",
      key: "partner_key",
      responsive: ["md"] as any,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      responsive: ["md"] as any,
    },
    {
      title: "Created at",
      dataIndex: "created_date",
      key: "created_date",
      responsive: ["md"] as any,
      render: (text: string) =>
        text ? moment(text).format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "Actions",
      key: "action",
      align: "right",
      render: (text: string, record: any) => (
        <Space size="middle">
          <Tooltip title="Test credential">
            <Button
              type="dashed"
              shape="round"
              loading={this.state.testing === record.uuid}
              onClick={() => this.testCredential(record)}
              icon={<ThunderboltOutlined />}
            />
          </Tooltip>
          <Tooltip title="Edit credential">
            <Button
              type="dashed"
              shape="round"
              onClick={() => this.props.editItem(record)}
              icon={<EditOutlined />}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this credential?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => this.deleteCredential(record)}
          >
            <Tooltip title="Delete credential">
              <Button type="dashed" shape="round" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ] as any;

  public load() {
    this.searchOptions = {
      search: {
        ...this.props.searchOptions.search,
      },
      options: {
        ...this.props.searchOptions.options,
      },
    };

    this.props.loadCredentials(this.searchOptions);
  }

  async testCredential(record: any) {
    this.setState({ testing: record.uuid });
    try {
      const response = await integrationCredentialTest({
        credential_uuid: record.uuid,
      });

      const body = response.data as any;

      // Transport/server error (network down, timeout, 5xx, no permission...).
      // This must NOT be reported as the credential itself being invalid.
      if (!response.ok || body?.prefix !== "2xx") {
        const reason = getResponseErrorReason(response);
        message.error(`Could not test the credential. Reason: ${reason}`);
        return;
      }

      // The request completed: trust the verdict returned by the API.
      const result = body?.data ?? body;
      if (result?.valid) {
        message.success(result?.message || "Credential is valid");
      } else {
        message.error(result?.message || "Credential is invalid");
      }
    } catch (e) {
      message.error("Could not test the credential. Please try again.");
    } finally {
      this.setState({ testing: null });
    }
  }

  async deleteCredential(record: any) {
    try {
      const response = await integrationCredentialDelete({
        credential_uuid: record.uuid,
      });

      const body = response.data as any;

      if (!response.ok || body?.prefix !== "2xx") {
        const reason = getResponseErrorReason(response);
        message.error(`Could not delete the credential. Reason: ${reason}`);
        return;
      }

      message.success("Credential deleted");
      this.setState({ removed: [...this.state.removed, record.uuid] });
      this.load();
    } catch (e) {
      message.error("Could not delete the credential. Please try again.");
    }
  }

  render() {
    const { loading, pagination, credentials, loadCredentials } = this.props;
    const { removed } = this.state;

    const data = credentials?.filter((v) => !removed.includes(v.uuid));
    return (
      <>
        <Table
          loading={loading}
          rowKey="uuid"
          columns={this.tableColumns}
          pagination={false}
          dataSource={data}
          onChange={(pagination: any, filters: any, sorter: any) =>
            this.handleTableChange(pagination, sorter, loadCredentials)
          }
        />
        <Pagination
          loading={loading}
          current={pagination?.page}
          pageSize={pagination?.limit}
          length={data?.length}
          onChange={(pagination) =>
            this.handleTableChange(pagination, undefined, loadCredentials)
          }
        />
      </>
    );
  }
}
